import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';

export const NavbarUltimate = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 50);
  });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Funktionen', id: 'features' },
    { label: 'Preise', id: 'pricing' },
    { label: 'Referenzen', id: 'testimonials' },
    { label: 'FAQ', id: 'faq' }
  ];

  return (
    <>
      <motion.header
        data-testid="landing-navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-[#0B1220]/95 backdrop-blur-xl border-b border-slate-800/50 shadow-lg' 
            : 'bg-transparent'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <Link 
              to="/" 
              data-testid="navbar-logo"
              className="flex items-center"
            >
              <motion.img 
                src="/cash-x-logo.png" 
                alt="Cash-X" 
                className="h-16 lg:h-20 w-auto object-contain"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  data-testid={`nav-${item.id}`}
                  className="relative px-4 py-2 text-slate-300 hover:text-white text-sm font-medium transition-colors group"
                >
                  {item.label}
                  <motion.span
                    className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:w-1/2 transition-all duration-300"
                  />
                </button>
              ))}
              <Link
                to="/app"
                data-testid="nav-login"
                className="px-4 py-2 text-slate-300 hover:text-white text-sm font-medium transition-colors"
              >
                Anmelden
              </Link>
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Button
                data-testid="nav-demo-cta"
                variant="ghost"
                className="text-slate-300 hover:text-white hover:bg-white/5"
                onClick={() => scrollToSection('cta')}
              >
                Demo anfragen
              </Button>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  data-testid="nav-primary-cta"
                  className="bg-gradient-to-r from-[#2F80FF] to-[#2DD4BF] hover:from-[#4A92FF] hover:to-[#34D9C4] text-white px-6 font-semibold border-0"
                  onClick={() => window.location.href = '/register'}
                >
                  Kostenlos testen
                </Button>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={mobileMenuOpen ? 'close' : 'open'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </motion.div>
              </AnimatePresence>
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-16 z-40 md:hidden"
          >
            <div className="bg-[#0B1220]/98 backdrop-blur-xl border-b border-slate-800 shadow-2xl">
              <div className="max-w-7xl mx-auto px-4 py-6 space-y-2">
                {navItems.map((item, i) => (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => scrollToSection(item.id)}
                    data-testid={`mobile-nav-${item.id}`}
                    className="block w-full text-left px-4 py-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                  >
                    {item.label}
                  </motion.button>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Link
                    to="/app"
                    data-testid="mobile-nav-login"
                    className="block px-4 py-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Anmelden
                  </Link>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="pt-4"
                >
                  <Button
                    data-testid="mobile-primary-cta"
                    className="w-full bg-gradient-to-r from-[#2F80FF] to-[#2DD4BF] text-white font-semibold h-12 border-0"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      window.location.href = '/register';
                    }}
                  >
                    Kostenlos testen
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavbarUltimate;
